import { lengthOrToken } from "@/purpose-sections/length";

export const CAROUSEL_EFFECTS = [
  "slide",
  "fade",
  "zoom",
  "rise",
  "kenBurns",
  "parallax",
  "none",
] as const;
export type CarouselEffect = (typeof CAROUSEL_EFFECTS)[number];
export const ARROW_STYLES = [
  "circle",
  "filled",
  "square",
  "block",
  "plain",
] as const;
export type ArrowStyle = (typeof ARROW_STYLES)[number];
export const ARROW_POSITIONS = ["below", "sides", "outside"] as const;
export type ArrowPosition = (typeof ARROW_POSITIONS)[number];
export const DOT_STYLES = ["dots", "bars", "counter"] as const;
export type DotStyle = (typeof DOT_STYLES)[number];
export const DOT_POSITIONS = ["below", "inside", "corner"] as const;
export type DotPosition = (typeof DOT_POSITIONS)[number];
export const CONTROLS_ALIGNS = ["left", "center", "right"] as const;
export type ControlsAlign = (typeof CONTROLS_ALIGNS)[number];
export const CONTROL_SIZES = { sm: 36, md: 44, lg: 60 } as const;
export type ControlSize = keyof typeof CONTROL_SIZES;
export const INTERVAL_RANGE = [2000, 60000] as const;
export const EFFECT_DURATION_RANGE = [300, 3000] as const;
/** Ken Burns' own transition when none is stored, whatever the type's default. */
export const KEN_BURNS_DURATION = 1500;

/** Every carousel key a section's `specifics` can hold; the CMS's builder offers the same list. */
export const CAROUSEL_KEYS = [
  "autoplay",
  "interval",
  "effect",
  "effectDuration",
  "loop",
  "columns",
  "peek",
  "columnGap",
  "showArrows",
  "arrowStyle",
  "arrowPosition",
  "showDots",
  "dotStyle",
  "dotPosition",
  "controlsAlign",
  "controlSize",
  "controlsGap",
  "arrowGap",
] as const;

export interface CarouselSettings {
  autoplay: boolean;
  /** how long a slide stands still before the next change starts, in ms */
  interval: number;
  effect: CarouselEffect;
  effectDuration: number;
  loop: boolean;
  /** slides per view on the phone and from 768 px */
  columns: [mobile: number, desktop: number];
  /** below 768 px the edge of the next slide shows */
  peek: boolean;
  columnGap: [mobile: string, desktop: string];
  showArrows: boolean;
  arrowStyle: ArrowStyle;
  arrowPosition: ArrowPosition;
  showDots: boolean;
  dotStyle: DotStyle;
  dotPosition: DotPosition;
  controlsAlign: ControlsAlign;
  controlSize: ControlSize;
  controlsGap: string;
  arrowGap: string;
  /** a type's behaviour, not a key: Kundeudtalelser's counter »1 / 3« between the arrows when it has no dots */
  counterBetweenArrows: boolean;
}

export interface CarouselOptions {
  defaults: CarouselSettings;
  effects: readonly CarouselEffect[];
  /** the most slides per view on each device; `[1, 1]` is one at a time */
  maxColumns: [mobile: number, desktop: number];
  /** values the editor cannot change: the JSON is ignored for them, and the CMS leaves them out */
  fixed?: Partial<CarouselSettings>;
}

export type CarouselDevice = "mobile" | "desktop";

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
const oneOf = <T extends string>(
  values: readonly T[],
  value: unknown,
  fallback: T,
): T =>
  (values as readonly unknown[]).includes(value) ? (value as T) : fallback;
const clamp = (value: number, [min, max]: readonly [number, number]) =>
  Math.min(max, Math.max(min, Math.round(value)));
const finite = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

function readColumns(
  raw: unknown,
  fallback: [number, number],
  max: [number, number],
): [number, number] {
  const parts =
    typeof raw === "number"
      ? [raw, raw]
      : typeof raw === "string"
        ? raw.split("|").map((part) => Number(part.trim()))
        : [];
  const [mobile, desktop = mobile] = parts;
  const one = (value: number | undefined, i: 0 | 1) =>
    Number.isInteger(value)
      ? Math.min(max[i], Math.max(1, value!))
      : Math.min(max[i], fallback[i]);
  return [one(mobile, 0), one(desktop, 1)];
}

function readGap(raw: unknown, fallback: [string, string]): [string, string] {
  if (typeof raw !== "string") return fallback;
  const [mobile, desktop = mobile] = raw.split("|");
  return [
    lengthOrToken(mobile) ?? fallback[0],
    lengthOrToken(desktop) ?? fallback[1],
  ];
}

/** Reads only the carousel keys from `specifics` with the type's options; it never throws and never writes. */
export function normalizeCarousel(
  specifics: unknown,
  options: CarouselOptions,
): CarouselSettings {
  const value = record(specifics);
  const d = options.defaults;
  const flag = (key: keyof CarouselSettings) =>
    typeof value[key] === "boolean"
      ? (value[key] as boolean)
      : (d[key] as boolean);
  const effect = oneOf(options.effects, value.effect, d.effect);
  const settings: CarouselSettings = {
    autoplay: flag("autoplay"),
    interval: finite(value.interval)
      ? clamp(value.interval, INTERVAL_RANGE)
      : d.interval,
    effect,
    effectDuration: finite(value.effectDuration)
      ? clamp(value.effectDuration, EFFECT_DURATION_RANGE)
      : effect === "kenBurns"
        ? KEN_BURNS_DURATION
        : d.effectDuration,
    loop: flag("loop"),
    columns: readColumns(value.columns, d.columns, options.maxColumns),
    peek: flag("peek"),
    columnGap: readGap(value.columnGap, d.columnGap),
    showArrows: flag("showArrows"),
    arrowStyle: oneOf(ARROW_STYLES, value.arrowStyle, d.arrowStyle),
    arrowPosition: oneOf(ARROW_POSITIONS, value.arrowPosition, d.arrowPosition),
    showDots: flag("showDots"),
    dotStyle: oneOf(DOT_STYLES, value.dotStyle, d.dotStyle),
    dotPosition: oneOf(DOT_POSITIONS, value.dotPosition, d.dotPosition),
    controlsAlign: oneOf(CONTROLS_ALIGNS, value.controlsAlign, d.controlsAlign),
    controlSize: oneOf(
      Object.keys(CONTROL_SIZES) as ControlSize[],
      value.controlSize,
      d.controlSize,
    ),
    controlsGap: lengthOrToken(value.controlsGap) ?? d.controlsGap,
    arrowGap: lengthOrToken(value.arrowGap) ?? d.arrowGap,
    counterBetweenArrows: d.counterBetweenArrows,
    ...options.fixed,
  };
  if (settings.effect !== "slide") {
    settings.columns = [1, 1];
    settings.peek = false;
  }
  return settings;
}

/** What a type draws for a key it does not set itself. */
export const CAROUSEL_BASE: CarouselSettings = {
  autoplay: false,
  interval: 5000,
  effect: "slide",
  effectDuration: 600,
  loop: true,
  columns: [1, 1],
  peek: false,
  columnGap: ["24px", "24px"],
  showArrows: true,
  arrowStyle: "circle",
  arrowPosition: "below",
  showDots: false,
  dotStyle: "dots",
  dotPosition: "below",
  controlsAlign: "center",
  controlSize: "sm",
  controlsGap: "20px",
  arrowGap: "10px",
  counterBetweenArrows: false,
};

const GALLERY: CarouselOptions = {
  defaults: { ...CAROUSEL_BASE, columns: [1, 3] },
  effects: ["slide", "fade", "zoom", "kenBurns", "none"],
  maxColumns: [5, 5],
};
const TESTIMONIALS: CarouselOptions = {
  defaults: {
    ...CAROUSEL_BASE,
    interval: 8000,
    columnGap: ["0px", "0px"],
    arrowStyle: "square",
    controlSize: "md",
    controlsGap: "0px",
    arrowGap: "16px",
    counterBetweenArrows: true,
  },
  effects: ["slide", "fade", "rise", "none"],
  maxColumns: [1, 1],
  fixed: { columns: [1, 1], peek: false, columnGap: ["0px", "0px"] },
};
const QUOTES: CarouselSettings = {
  ...CAROUSEL_BASE,
  autoplay: true,
  interval: 8000,
  effect: "fade",
  effectDuration: 1000,
  showDots: true,
  controlSize: "md",
  controlsGap: "24px",
  arrowGap: "12px",
};
const QUOTE_EFFECTS = ["fade", "slide", "rise", "none"] as const;
const PICTURE_QUOTE_EFFECTS = [
  "fade",
  "slide",
  "rise",
  "zoom",
  "kenBurns",
  "none",
] as const;
const ONE_AT_A_TIME: Partial<CarouselSettings> = {
  columns: [1, 1],
  peek: false,
};
const QUOTE_CAROUSEL: CarouselOptions[] = [
  {
    defaults: QUOTES,
    effects: QUOTE_EFFECTS,
    maxColumns: [1, 1],
    fixed: ONE_AT_A_TIME,
  },
  {
    defaults: QUOTES,
    effects: PICTURE_QUOTE_EFFECTS,
    maxColumns: [1, 1],
    fixed: ONE_AT_A_TIME,
  },
  {
    defaults: {
      ...QUOTES,
      arrowStyle: "filled",
      arrowPosition: "sides",
      dotPosition: "inside",
    },
    effects: PICTURE_QUOTE_EFFECTS,
    maxColumns: [1, 1],
    fixed: ONE_AT_A_TIME,
  },
  {
    defaults: {
      ...QUOTES,
      interval: 5000,
      effect: "slide",
      effectDuration: 600,
      columns: [1, 3],
      peek: true,
    },
    effects: ["slide", "fade", "rise", "none"],
    maxColumns: [2, 4],
  },
  { defaults: QUOTES, effects: QUOTE_EFFECTS, maxColumns: [1, 2] },
];

/** Skuffesager's Karrusel: cards that glide and stop at the ends; it never changes by itself. */
const MANUAL_LISTINGS: CarouselOptions = {
  defaults: { ...CAROUSEL_BASE, loop: false, columns: [1, 3], peek: true },
  effects: ["slide"],
  maxColumns: [2, 5],
  fixed: { autoplay: false, effect: "slide" },
};

/** Medier og tekst – avanceret's grid cells with several media: they cross-fade by themselves, one at a time, with the
 *  pause button in the corner; standing still they show arrows. */
const MEDIA_CELLS: CarouselOptions = {
  defaults: {
    ...CAROUSEL_BASE,
    autoplay: true,
    effect: "fade",
    effectDuration: 1000,
    showArrows: false,
    arrowStyle: "filled",
    arrowPosition: "sides",
    dotPosition: "corner",
  },
  effects: ["fade", "slide", "zoom", "rise", "kenBurns", "none"],
  maxColumns: [1, 1],
  fixed: {
    loop: true,
    columns: [1, 1],
    peek: false,
    columnGap: ["0px", "0px"],
    arrowStyle: "filled",
    arrowPosition: "sides",
    dotStyle: "dots",
    dotPosition: "corner",
    controlSize: "sm",
  },
};

/** The options of every type that carousels, by family and layout; undefined where the layout does not carousel. */
export function carouselOptions(
  family: string,
  variant: number,
): CarouselOptions | undefined {
  if (family === "gallery") return variant === 2 ? GALLERY : undefined;
  if (family === "manual-listings")
    return variant === 2 ? MANUAL_LISTINGS : undefined;
  if (family === "testimonials")
    return variant === 2 ? TESTIMONIALS : undefined;
  if (family === "quote-carousel") return QUOTE_CAROUSEL[variant];
  if (family === "image-text-advanced") return MEDIA_CELLS;
  return undefined;
}

/** `track`: a scrolling strip, for Glid with several per view (or a peek on the phone); `stack`: one at a time. */
export function carouselMode(
  s: CarouselSettings,
  device: CarouselDevice,
): "track" | "stack" {
  const perView = s.columns[device === "mobile" ? 0 : 1];
  return s.effect === "slide" &&
    (perView > 1 || (device === "mobile" && s.peek))
    ? "track"
    : "stack";
}

export function carouselPositions(count: number, perView: number): number {
  return Math.max(1, count - perView + 1);
}

/** The position after one step, wrapping with `loop`; `null` at an end without it. */
export function carouselStep(
  index: number,
  step: number,
  positions: number,
  loop: boolean,
): number | null {
  const next = index + step;
  if (next >= 0 && next < positions) return next;
  return loop ? ((next % positions) + positions) % positions : null;
}

/** From the start of one automatic change to the start of the next: the standing time plus the transition. */
export function carouselCycle(
  s: CarouselSettings,
  reducedMotion: boolean,
): number {
  return (
    s.interval + (reducedMotion || s.effect === "none" ? 0 : s.effectDuration)
  );
}

/** Arrows show when switched on, and whenever it stands still without dots or bars to navigate with. */
export function arrowsShown(
  s: CarouselSettings,
  standingStill: boolean,
): boolean {
  return (
    s.showArrows || (standingStill && (!s.showDots || s.dotStyle === "counter"))
  );
}

export function carouselStyle(
  s: CarouselSettings,
): Record<string, string | number> {
  return {
    "--re-carousel-columns": s.columns[0],
    "--re-carousel-columns-desktop": s.columns[1],
    "--re-carousel-gap": s.columnGap[0],
    "--re-carousel-gap-desktop": s.columnGap[1],
    "--re-carousel-duration": `${s.effectDuration}ms`,
    "--re-carousel-interval": `${s.interval}ms`,
    "--re-carousel-control-size": `${CONTROL_SIZES[s.controlSize]}px`,
    "--re-carousel-controls-gap": s.controlsGap,
    "--re-carousel-arrow-gap": s.arrowGap,
  };
}

export function carouselAttributes(
  s: CarouselSettings,
): Record<string, string> {
  return {
    "data-effect": s.effect,
    "data-mode-mobile": carouselMode(s, "mobile"),
    "data-mode-desktop": carouselMode(s, "desktop"),
    ...(s.peek ? { "data-peek": "true" } : {}),
    "data-arrow-style": s.arrowStyle,
    "data-arrow-position": s.arrowPosition,
    "data-dot-style": s.dotStyle,
    "data-dot-position": s.dotPosition,
    "data-controls-align": s.controlsAlign,
  };
}

export interface CarouselNoun {
  one: string;
  many: string;
}

/** The controls' Danish names (the WAI carousel pattern) for a noun such as `{ one: "citat", many: "citater" }`. */
export function carouselLabels({ one }: CarouselNoun) {
  const capital = one.charAt(0).toUpperCase() + one.slice(1);
  const named = (text: string, label?: string) =>
    label ? `${text}: ${label}` : text;
  return {
    previous: `Forrige ${one}`,
    next: `Næste ${one}`,
    dots: `Vælg ${one}`,
    dot: (n: number, label?: string) => named(`Vis ${one} ${n}`, label),
    pause: "Sæt automatisk skift på pause",
    play: "Start automatisk skift",
    slide: (n: number, count: number, label?: string) =>
      named(`${n} af ${count}`, label),
    status: (from: number, to: number, count: number) =>
      from === to
        ? `${capital} ${from} af ${count}`
        : `${capital} ${from} til ${to} af ${count}`,
  };
}

/** 1 (next) for a move left of at least 40 px, -1 for one to the right, 0 for a shorter or mostly vertical move. */
export function swipeDirection(dx: number, dy: number): -1 | 0 | 1 {
  if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return 0;
  return dx < 0 ? 1 : -1;
}
